import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useAuth } from "../../contexts/AuthContext";
import { logout, updateUserPhone, updateUserPassword, getAccountInfo } from "../../api/auth";
import { getGoogleCalendarStatus } from "../../api/calendar";
import logo from "../../assets/images/logo_text.png";
import { HiChevronDown } from "react-icons/hi";


const Button = ({text="버튼", backgroundColor='#EFEFEF', textColor='#0665FD', onClick}) => {
    return (
        <button
            onClick={() => { onClick(); }}
            style={{
                padding: '7px 14px',
                borderRadius: '8px',
                gap: '4px',
                background: backgroundColor,
                cursor: 'pointer',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
            }}
        >
            <span style={{ fontSize: '14px', fontFamily: 'Pretendard', fontWeight: '600', color: textColor }}>
                {text}
            </span>
        </button>
    );
};


const Header = () => {
    const navigate = useNavigate();
    const { isLoggedIn, setIsLoggedIn, user } = useAuth();
    const [캘린더연동됨, set캘린더연동됨] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [휴대전화번호, set휴대전화번호] = useState('');
    const [전화번호모달열림, set전화번호모달열림] = useState(false);
    const [전화번호입력값, set전화번호입력값] = useState('');
    const [전화번호저장중, set전화번호저장중] = useState(false);
    const [비밀번호모달열림, set비밀번호모달열림] = useState(false);
    const [현재비밀번호, set현재비밀번호] = useState('');
    const [새비밀번호, set새비밀번호] = useState('');
    const [새비밀번호확인, set새비밀번호확인] = useState('');
    const [비밀번호저장중, set비밀번호저장중] = useState(false);
    const [비밀번호에러, set비밀번호에러] = useState('');

    useEffect(() => {
        if (!isLoggedIn) return;
        getGoogleCalendarStatus()
            .then((데이터) => { set캘린더연동됨(데이터?.connected === true); })
            .catch(() => {});
    }, [isLoggedIn]);

    useEffect(() => {
        if (!isLoggedIn) return;
        getAccountInfo()
            .then((res) => {
                const phone = res?.data?.account?.phone_number;
                if (phone) set휴대전화번호(phone);
            })
            .catch(() => {});
    }, [isLoggedIn]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLogout = async () => {
        try {
            await logout();
            setIsLoggedIn(false);
            setDropdownOpen(false);
            setTimeout(() => { navigate("/", { replace: true }); }, 50);
        } catch (error) {
            alert("로그아웃 중 오류가 발생했습니다.");
        }
    };

    const handlePhoneModalOpen = () => {
        set전화번호입력값(휴대전화번호);
        set전화번호모달열림(true);
        setDropdownOpen(false);
    };

    const handleCalendarConnect = () => {
        const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
        const redirectUri = `${window.location.origin}/accounts/me/calendar/google/callback`;
        const scope = 'https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events';
        sessionStorage.setItem('calendarConnectReturnTo', window.location.pathname);
        setDropdownOpen(false);
        const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(scope)}&access_type=offline&prompt=consent`;
        window.location.href = url;
    };

    const handlePasswordModalOpen = () => {
        set현재비밀번호('');
        set새비밀번호('');
        set새비밀번호확인('');
        set비밀번호에러('');
        set비밀번호모달열림(true);
        setDropdownOpen(false);
    };

    const handlePasswordSave = async () => {
        if (새비밀번호 !== 새비밀번호확인) {
            set비밀번호에러('새 비밀번호가 일치하지 않습니다.');
            return;
        }
        if (새비밀번호.length < 8) {
            set비밀번호에러('비밀번호는 8자 이상이어야 합니다.');
            return;
        }
        if (비밀번호저장중) return;
        set비밀번호저장중(true);
        set비밀번호에러('');
        try {
            await updateUserPassword({ current_password: 현재비밀번호, new_password: 새비밀번호 });
            set비밀번호모달열림(false);
        } catch {
            set비밀번호에러('비밀번호 변경에 실패했습니다. 현재 비밀번호를 확인해주세요.');
        } finally {
            set비밀번호저장중(false);
        }
    };

    const handlePhoneSave = async () => {
        if (전화번호저장중) return;
        set전화번호저장중(true);
        try {
            await updateUserPhone(전화번호입력값.replace(/-/g, ''));
            set휴대전화번호(전화번호입력값);
            set전화번호모달열림(false);
        } catch {
        } finally {
            set전화번호저장중(false);
        }
    };


    return (
        <div
            style={{
                width: '100dvw',
                height: '56px',
                borderBottom: '1px solid #eeeeee',
                position: 'fixed',
                top: 0,
                left: 0,
                padding: '0 16px',
                boxSizing: 'border-box',
                background: '#ffffff',
                zIndex: 100,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: '#111111'
            }}
        >
            {전화번호모달열림 && (
                <PhoneModalOverlay onClick={() => set전화번호모달열림(false)}>
                    <PhoneModalBox onClick={(e) => e.stopPropagation()}>
                        <PhoneModalTitle>휴대전화번호 연동</PhoneModalTitle>
                        <PhoneModalDesc>알림 및 요약을 수신할 전화번호를 입력해주세요.</PhoneModalDesc>
                        <PhoneModalInput
                            type="tel"
                            placeholder="010-0000-0000"
                            value={전화번호입력값}
                            onChange={(e) => {
                                const digits = e.target.value.replace(/\D/g, '');
                                let formatted = digits;
                                if (digits.length <= 3) formatted = digits;
                                else if (digits.length <= 7) formatted = `${digits.slice(0,3)}-${digits.slice(3)}`;
                                else formatted = `${digits.slice(0,3)}-${digits.slice(3,7)}-${digits.slice(7,11)}`;
                                set전화번호입력값(formatted);
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter') handlePhoneSave(); }}
                            autoFocus
                        />
                        <PhoneModalButtons>
                            <PhoneModalCancel onClick={() => set전화번호모달열림(false)}>취소</PhoneModalCancel>
                            <PhoneModalConfirm onClick={handlePhoneSave} disabled={전화번호저장중}>
                                {전화번호저장중 ? '저장 중...' : '저장'}
                            </PhoneModalConfirm>
                        </PhoneModalButtons>
                    </PhoneModalBox>
                </PhoneModalOverlay>
            )}

            {비밀번호모달열림 && (
                <PhoneModalOverlay onClick={() => set비밀번호모달열림(false)}>
                    <PhoneModalBox onClick={(e) => e.stopPropagation()}>
                        <PhoneModalTitle>비밀번호 변경</PhoneModalTitle>
                        <PhoneModalDesc>현재 비밀번호를 확인한 후 새 비밀번호를 설정하세요.</PhoneModalDesc>
                        <PhoneModalInput
                            type="password"
                            placeholder="현재 비밀번호"
                            value={현재비밀번호}
                            onChange={(e) => set현재비밀번호(e.target.value)}
                            autoFocus
                        />
                        <PhoneModalInput
                            type="password"
                            placeholder="새 비밀번호 (8자 이상)"
                            value={새비밀번호}
                            onChange={(e) => set새비밀번호(e.target.value)}
                        />
                        <PhoneModalInput
                            type="password"
                            placeholder="새 비밀번호 확인"
                            value={새비밀번호확인}
                            onChange={(e) => set새비밀번호확인(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handlePasswordSave(); }}
                            style={{ marginBottom: 비밀번호에러 ? '4px' : '8px' }}
                        />
                        {비밀번호에러 && (
                            <span style={{ fontSize: '12px', color: '#EF4444', marginBottom: '8px' }}>{비밀번호에러}</span>
                        )}
                        <PhoneModalButtons>
                            <PhoneModalCancel onClick={() => set비밀번호모달열림(false)}>취소</PhoneModalCancel>
                            <PhoneModalConfirm onClick={handlePasswordSave} disabled={비밀번호저장중}>
                                {비밀번호저장중 ? '변경 중...' : '변경'}
                            </PhoneModalConfirm>
                        </PhoneModalButtons>
                    </PhoneModalBox>
                </PhoneModalOverlay>
            )}

            <img src={logo} alt="띠링" style={{ height: '24px', display: 'block' }} />

            <>
                {isLoggedIn ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <WorkspaceWrapper ref={dropdownRef}>
                            <WorkspaceButton onClick={() => setDropdownOpen(o => !o)}>
                                <WorkspaceName>워크스페이스</WorkspaceName>
                                <HiChevronDown size={16} color="#888888" />
                            </WorkspaceButton>

                            {dropdownOpen && (
                                <Dropdown>
                                    <DropdownProfile>
                                        <ProfileAvatar />
                                        <ProfileInfo>
                                            <ProfileLabel>현재 로그인 계정</ProfileLabel>
                                            <ProfileEmail>{user?.email || '-'}</ProfileEmail>
                                        </ProfileInfo>
                                        <LogoutButton onClick={handleLogout}>로그아웃</LogoutButton>
                                    </DropdownProfile>

                                    <DropdownDivider />

                                    <DropdownItem onClick={!캘린더연동됨 ? handleCalendarConnect : undefined}>
                                        <DropdownItemLabel>구글 캘린더</DropdownItemLabel>
                                        {캘린더연동됨 ? (
                                            <CalendarBadge>
                                                <CalendarDot />
                                                연동됨
                                            </CalendarBadge>
                                        ) : (
                                            <Badge $color="#fff3e0" $text="#e67e00">미연동</Badge>
                                        )}
                                    </DropdownItem>

                                    <PhoneItem onClick={handlePhoneModalOpen}>
                                        <DropdownItemLabel>휴대전화번호</DropdownItemLabel>
                                        <PhoneValue>
                                            {휴대전화번호 || <PhonePlaceholder>미입력</PhonePlaceholder>}
                                        </PhoneValue>
                                    </PhoneItem>

                                    <DropdownItem onClick={handlePasswordModalOpen}>
                                        <DropdownItemLabel>비밀번호 변경</DropdownItemLabel>
                                    </DropdownItem>

                                    <DropdownDivider />

                                    <DropdownItem>
                                        <DropdownItemLabel>구독정보</DropdownItemLabel>
                                        <Badge $color="#e8e8e8" $text="#555555">파트너님을 응원해요</Badge>
                                    </DropdownItem>
                                    <DropdownItem>
                                        <DropdownItemLabel>구매내역</DropdownItemLabel>
                                    </DropdownItem>
                                    <DropdownItem>
                                        <DropdownItemLabel>문의하기</DropdownItemLabel>
                                    </DropdownItem>

                                    <DropdownDivider />

                                    <DropdownItem $danger>
                                        <DropdownItemLabel $danger>회원탈퇴</DropdownItemLabel>
                                    </DropdownItem>
                                </Dropdown>
                            )}
                        </WorkspaceWrapper>
                    </div>
                ) : (
                    <Button text="회원가입/로그인" onClick={() => { navigate("/auth/login"); }} />
                )}
            </>
        </div>
    );
};

const WorkspaceWrapper = styled.div`
    position: relative;
`;

const WorkspaceButton = styled.button`
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 42px;
    padding: 11px 10px;
    border: none;
    border-radius: 8px;
    box-sizing: border-box;
    background: transparent;
    cursor: pointer;
    max-width: 180px;
    outline: 0;
    transition: background 0.12s;

    &:hover { background: #f5f5f5; }
`;

const WorkspaceName = styled.span`
    margin: 0;
    padding: 0;
    font-style: normal;
    font-family: Pretendard Variable, Pretendard, Lato, "Noto Sans KR", "nanum gothic", sans-serif;
    font-feature-settings: "cv13", "ss06", "tnum";
    font-size: 13px;
    font-weight: 500;
    color: #333333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const Dropdown = styled.div`
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    width: 280px;
    background: #ffffff;
    border: 1px solid #eeeeee;
    border-radius: 14px;
    padding: 8px 0;
    box-shadow: 0 8px 24px rgba(0,0,0,0.08);
    z-index: 200;
`;

const DropdownProfile = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
`;

const ProfileAvatar = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #eeeeee;
    flex-shrink: 0;
`;

const ProfileInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
`;

const ProfileLabel = styled.span`
    font-family: Pretendard, sans-serif;
    font-size: 11px;
    color: #aaaaaa;
`;

const ProfileEmail = styled.span`
    font-family: Pretendard, sans-serif;
    font-size: 13px;
    font-weight: 700;
    color: #111111;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const LogoutButton = styled.button`
    background: none;
    border: none;
    font-family: Pretendard, sans-serif;
    font-size: 12px;
    color: #aaaaaa;
    cursor: pointer;
    padding: 0;
    flex-shrink: 0;

    &:hover { color: #333333; }
`;

const DropdownDivider = styled.div`
    height: 1px;
    background: #f0f0f0;
    margin: 4px 0;
`;

const DropdownItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 12px 16px;
    cursor: pointer;
    transition: background 0.1s;

    &:hover { background: #f7f8fa; }
`;

const DropdownItemLabel = styled.span`
    font-family: Pretendard, sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: ${({ $danger }) => $danger ? '#ff4d4d' : '#222222'};
`;

const CalendarBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-family: Pretendard, sans-serif;
    font-size: 11px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 20px;
    background: #e8f5e9;
    color: #2e7d32;
    flex-shrink: 0;
`;

const CalendarDot = styled.div`
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #4CAF50;
    flex-shrink: 0;
`;

const PhoneItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 12px 16px;
    cursor: pointer;
    transition: background 0.1s;

    &:hover { background: #f7f8fa; }
`;

const PhoneValue = styled.span`
    font-family: Pretendard, sans-serif;
    font-size: 13px;
    font-weight: 500;
    color: #111111;
`;

const PhonePlaceholder = styled.span`
    color: #cccccc;
    font-weight: 400;
`;

const PhoneModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 500;
`;

const PhoneModalBox = styled.div`
    background: #ffffff;
    border-radius: 16px;
    padding: 28px 24px 24px;
    width: 320px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
`;

const PhoneModalTitle = styled.div`
    font-family: Pretendard, sans-serif;
    font-size: 16px;
    font-weight: 700;
    color: #111111;
    margin-bottom: 2px;
`;

const PhoneModalDesc = styled.div`
    font-family: Pretendard, sans-serif;
    font-size: 13px;
    color: #888888;
    margin-bottom: 8px;
    line-height: 1.5;
`;

const PhoneModalInput = styled.input`
    height: 48px;
    padding: 0 14px;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    font-family: Pretendard, sans-serif;
    font-size: 15px;
    color: #111111;
    outline: none;
    transition: border-color 0.15s;
    margin-bottom: 8px;

    &::placeholder { color: #cccccc; }
    &:focus { border-color: #487BFD; }
`;

const PhoneModalButtons = styled.div`
    display: flex;
    gap: 8px;
`;

const PhoneModalCancel = styled.button`
    flex: 1;
    height: 44px;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    background: #ffffff;
    font-family: Pretendard, sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: #555555;
    cursor: pointer;
    transition: background 0.12s;

    &:hover { background: #f5f5f5; }
`;

const PhoneModalConfirm = styled.button`
    flex: 1;
    height: 44px;
    border: none;
    border-radius: 10px;
    background: #487BFD;
    font-family: Pretendard, sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: #ffffff;
    cursor: pointer;
    transition: background 0.12s;

    &:hover { background: #3568e8; }
    &:disabled { background: #aaaaaa; cursor: default; }
`;

const Badge = styled.span`
    font-family: Pretendard, sans-serif;
    font-size: 11px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 20px;
    background: ${({ $color }) => $color};
    color: ${({ $text }) => $text};
    flex-shrink: 0;
`;

export default Header;
